<!-- The customer message thread. Extracted from conversation.html so the Live
     Conversations page and the floating chat dock render the SAME thread. The host owns
     the surrounding chrome; everything here is keyed off [conversation]. -->
    <!-- ── COL 2: Chat ──────────────────────────────────────────── -->
    <section class="conv-chat">
      <!-- Empty state when nothing conversation -->
      <div class="conv-empty" *ngIf="!conversation">
        <i class="pi pi-comments"></i>
        <h3>Select a conversation</h3>
        <p>Click any conversation on the left to view the chat and workflow details.</p>
      </div>

      <ng-container *ngIf="conversation">

        <!-- Dropped in compact mode (the chat dock) - see ChatThreadComponent.compact. -->
        <div class="conv-chat__header" *ngIf="!compact">
          <div class="conv-chat__who">
            <div class="conv-chat__avatar">{{ convInitials(conversation) }}</div>
            <div>
              <div class="conv-chat__name">{{ convTitle(conversation) }}</div>
              <div class="conv-chat__sub">{{ convSubtitle(conversation) || conversation.flowName }}</div>
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:8px;">
            <!-- Hand over toggle: escalates the conversation (AI stops replying) / hands it back. -->
            <button pButton type="button" [outlined]="conversation.handoff !== 'ACTIVE'" size="small"
                    [severity]="conversation.handoff === 'ACTIVE' ? 'secondary' : 'warn'"
                    [icon]="conversation.handoff === 'ACTIVE' ? 'pi pi-sparkles' : 'pi pi-user'"
                    [label]="conversation.handoff === 'ACTIVE' ? 'Hand back to AI' : 'Hand over to agent'"
                    [disabled]="togglingHandoff" (click)="toggleHandoff()"></button>
            <span *ngIf="conversation.handoff === 'ACTIVE'" title="AI is paused — the team owns this chat"
                  style="font-size:10px;font-weight:700;color:#b45309;background:#fef3c7;border-radius:999px;padding:2px 8px;">WITH TEAM</span>
            <div class="status-badge" [ngClass]="conversationStatus">{{ conversationStatus | titlecase }}</div>
          </div>
        </div>

        <!-- wheel/touch/key are bound separately from (scroll): the scroll handler ignores a
             short window after our own programmatic jump, so these carry the "the user really did
             grab it" signal that cancels the re-pin. -->
        <div class="conv-chat__messages chat-message-list" #chatContainer (scroll)="onScroll()"
             (wheel)="cancelPin()" (touchstart)="cancelPin()" (keydown)="cancelPin()">
          <!-- Scrollback. Only the newest page loads with the chat, so without this an agent
               opening a long conversation lands in the middle of it with no way back. Scrolling to
               the top loads a page on its own; this is the visible affordance for that. -->
          <div class="chat-earlier" *ngIf="hasEarlier">
            <button type="button" class="chat-earlier__btn" (click)="loadEarlierMessages()"
                    [disabled]="loadingEarlier">
              <i class="pi" [ngClass]="loadingEarlier ? 'pi-spinner pi-spin' : 'pi-history'"></i>
              {{ loadingEarlier ? 'Loading…' : 'Load earlier messages' }}
            </button>
          </div>
          <ng-container *ngFor="let msg of messages; trackBy: trackMsg">
            <div class="message-row" [ngClass]="msg.direction === 'OUTBOUND' ? 'outbound' : 'inbound'">
              <div class="bubble" [ngClass]="msg.direction === 'OUTBOUND' ? 'agent-bubble' : 'user-bubble'"
                   [class.media-bubble]="msg.content?.kind === 'IMAGE' || msg.content?.kind === 'STICKER' || msg.content?.kind === 'VIDEO'"
                   [class.sticker-bubble]="msg.content?.kind === 'STICKER'">

                <!-- Image / sticker -->
                <ng-container *ngIf="msg.content?.kind === 'IMAGE' || msg.content?.kind === 'STICKER'">
                  <img *ngIf="mediaSrc(msg) as src" [src]="src" class="chat-image" loading="lazy"
                       [class.is-loaded]="msg.__mediaLoaded"
                       (load)="msg.__mediaLoaded = true; onChatMediaLoaded()"
                       (error)="msg.__mediaLoaded = true"
                       [class.chat-sticker]="msg.content?.kind === 'STICKER'" (click)="openImage(msg)" />
                </ng-container>

                <!-- Voice note / audio -->
                <div class="chat-audio" *ngIf="msg.content?.kind === 'AUDIO'">
                  <i class="pi" [ngClass]="msg.content?.voice ? 'pi-microphone' : 'pi-volume-up'"></i>
                  <audio *ngIf="mediaSrc(msg) as src" [src]="src" controls preload="none"
                         (loadedmetadata)="onChatMediaLoaded()"></audio>
                </div>

                <!-- Video -->
                <div class="chat-video" *ngIf="msg.content?.kind === 'VIDEO'">
                  <video *ngIf="mediaSrc(msg) as src" [src]="src" controls preload="none"
                         (loadedmetadata)="onChatMediaLoaded()"></video>
                </div>

                <!-- Document — force download (never inline-render: a self-hosted file could be
                     HTML/SVG; download saves it, and cross-origin blocks token access either way). -->
                <a class="chat-doc" *ngIf="msg.content?.kind === 'DOCUMENT'" [href]="mediaSrc(msg)"
                   [download]="msg.content?.filename || 'document'" rel="noopener">
                  <i class="pi pi-file"></i>
                  <span class="chat-doc__name">{{ msg.content?.filename || 'Document' }}</span>
                  <i class="pi pi-download dl"></i>
                </a>

                <!-- Location pin -->
                <a class="chat-loc" *ngIf="msg.content?.kind === 'LOCATION'" [href]="mapUrl(msg)"
                   target="_blank" rel="noopener">
                  <div class="chat-loc__map"><i class="pi pi-map-marker"></i></div>
                  <span class="chat-loc__name">{{ msg.content?.locationName || 'Shared location' }}</span>
                </a>

                <!-- Caption / text -->
                <p class="msg-text" *ngIf="getMessageText(msg)">{{ getMessageText(msg) }}</p>

                <!-- Unsupported / empty -->
                <span class="empty-message" *ngIf="isEmptyMessage(msg)">Unsupported message</span>

                <!-- Quick-reply buttons -->
                <div *ngIf="msg.content?.buttons?.length" class="quick-replies">
                  <button *ngFor="let btn of msg.content.buttons" class="qr-btn"
                    [disabled]="isFreePlan" (click)="onQuickReply(btn)">{{ btn.text }}</button>
                </div>

                <!-- WhatsApp-style receipts: ✓ sent · ✓✓ delivered · blue ✓✓ read · red tag failed -->
                <span class="meta">{{ getMessageTime(msg) }}<ng-container *ngIf="msg.direction === 'OUTBOUND'"><span
                      *ngIf="!isFailed(msg)" class="ticks" [class.ticks--read]="tickState(msg) === 'read'"
                      [title]="tickTitle(msg)"><i class="pi pi-check tick"></i><i
                      *ngIf="tickState(msg) !== 'sent'" class="pi pi-check tick tick--second"></i></span><span *ngIf="isFailed(msg)"
                      class="failed-tag" [title]="failureTooltip(msg)"><i class="pi pi-exclamation-circle"></i> not delivered</span></ng-container></span>

                <!-- Why it did not arrive, straight from Meta. Before 2026-09-12 the reason was
                     logged on the server and discarded, so the red tag above was all an agent
                     ever saw; they would resend, which makes a quality throttle worse. -->
                <div class="failed-reason" *ngIf="isFailed(msg) && failureText(msg)">
                  <i class="pi pi-info-circle"></i><span>{{ failureText(msg) }}</span>
                </div>
              </div>
            </div>
          </ng-container>
        </div>

        <ng-container *ngIf="conversationStatus === 'active'">
          <!-- 24h window CLOSED — free text can't be delivered; offer an approved template instead -->
          <div class="window-closed" *ngIf="windowChecked && !windowOpen">
            <div class="window-closed__msg">
              <i class="pi pi-clock"></i>
              <span>The 24-hour window is closed<ng-container *ngIf="windowClosedSince"> — customer last replied {{ windowClosedSince }}</ng-container>. Free text won’t be delivered; send an approved template to re-open the chat.</span>
            </div>
            <p-button label="Choose template" icon="pi pi-file-edit" size="small"
              [disabled]="isFreePlan" (click)="openTemplatePicker()" />
          </div>

          <!-- Window OPEN (or not yet checked) — normal free-text reply.
               Attach + mic send real media messages (WhatsApp session media / widget SSE). -->
          <div class="conv-chat__files" *ngIf="pendingFiles.length">
            <span class="conv-chat__file" *ngFor="let f of pendingFiles; let i = index">
              <i class="pi" [ngClass]="f.type.startsWith('image/') ? 'pi-image' : 'pi-file'"></i>
              <span class="conv-chat__file-name">{{ f.name }}</span>
              <i class="pi pi-times conv-chat__file-x" (click)="removePendingFile(i)"></i>
            </span>
          </div>
          <div class="conv-chat__input" *ngIf="!windowChecked || windowOpen">
            <input #convFile type="file" hidden multiple
                   accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,video/*,audio/*"
                   (change)="onComposerAttach($event)" />
            <button type="button" class="conv-attach-btn" pTooltip="Attach photos, videos, documents or audio"
                    tooltipPosition="top" [disabled]="isFreePlan" (click)="convFile.click()">
              <i class="pi pi-paperclip"></i>
            </button>
            <button type="button" class="conv-attach-btn" [class.conv-attach-btn--rec]="recording"
                    [pTooltip]="recording ? 'Stop & send voice note' : 'Record a voice note'"
                    tooltipPosition="top" [disabled]="isFreePlan" (click)="toggleRecording()">
              <i class="pi" [ngClass]="recording ? 'pi-stop-circle' : 'pi-microphone'"></i>
              <span *ngIf="recording" class="conv-rec-secs">{{ recordSecs }}s</span>
            </button>
            <input type="text" pInputText [(ngModel)]="messageInput" (keyup.enter)="sendMessage()"
              placeholder="Type a message…"
              [disabled]="isFreePlan" pTooltip="Upgrade to Pro plan" [tooltipDisabled]="!isFreePlan" />
            <p-button icon="pi pi-send" [rounded]="true" severity="primary" size="small"
              [disabled]="isFreePlan || (!messageInput.trim() && !pendingFiles.length)" (click)="sendMessage()" />
          </div>
        </ng-container>
      </ng-container>
    </section>

<p-dialog header="Send a template" [(visible)]="showTemplateDialog" [modal]="true"
          [style]="{width: '560px'}" [draggable]="false" [dismissableMask]="true" styleClass="tmpl-dialog">
  <div class="tmpl-picker">
    <p class="tmpl-hint">The 24-hour window is closed, so only an approved template will be delivered. Pick one and fill in any variables.</p>

    <p class="tmpl-loading" *ngIf="templatesLoading"><i class="pi pi-spin pi-spinner"></i> Loading templates…</p>
    <p class="tmpl-empty" *ngIf="!templatesLoading && !templates.length">
      No approved templates yet. Create one and get it approved by Meta under <b>Templates</b> first.
    </p>

    <!-- Approved template list -->
    <div class="tmpl-list" *ngIf="!templatesLoading && templates.length">
      <button type="button" class="tmpl-item" *ngFor="let t of templates"
              [class.conversation]="selectedTemplate?.id === t.id" (click)="selectTemplate(t)">
        <div class="tmpl-item__top">
          <span class="tmpl-item__name">{{ t.name }}</span>
          <span class="tmpl-item__meta">{{ t.language }}<ng-container *ngIf="t.category"> · {{ t.category }}</ng-container></span>
        </div>
        <div class="tmpl-item__body">{{ bodyText(t) }}</div>
      </button>
    </div>

    <!-- Fill header media + variables + preview -->
    <div class="tmpl-fill" *ngIf="selectedTemplate">

      <!-- Media header (image / video / document) -->
      <div class="tmpl-header" *ngIf="needsHeaderMedia(selectedTemplate)">
        <label>Header {{ headerFormatSel | lowercase }}</label>
        <div class="tmpl-header__row">
          <input type="file" [accept]="headerAccept()" (change)="onHeaderFile($event)" [disabled]="uploadingHeader" />
          <span class="tmpl-header__status" *ngIf="uploadingHeader"><i class="pi pi-spin pi-spinner"></i> uploading…</span>
          <span class="tmpl-header__status ok" *ngIf="!uploadingHeader && headerMediaUrl"><i class="pi pi-check"></i> {{ headerFileName || 'ready' }}</span>
        </div>
        <img *ngIf="headerMediaUrl && headerFormatSel === 'IMAGE'" [src]="headerMediaUrl" class="tmpl-header__preview" />
      </div>

      <!-- Body variables (positional {{ '{{' }}1{{ '}}' }} or named) -->
      <div class="tmpl-var" *ngFor="let v of templateVars">
        <label>{{ v.label }}</label>
        <input type="text" pInputText [(ngModel)]="v.value" [placeholder]="'Value for ' + v.label" />
      </div>
      <!-- Dynamic URL button — the link part appended into the template's button url. -->
      <div class="tmpl-var" *ngIf="templateHasDynamicUrlButton(selectedTemplate)">
        <label><i class="pi pi-link"></i> Button link value</label>
        <input type="text" pInputText [(ngModel)]="templateUrlButtonValue"
               placeholder="Value for the button's {{ '{{' }}1{{ '}}' }} (e.g. an id or path)" />
      </div>

      <!-- Preview -->
      <div class="tmpl-preview" *ngIf="bodyText(selectedTemplate)">
        <div class="tmpl-preview__label">Preview</div>
        <div class="tmpl-preview__bubble">{{ templatePreview() }}</div>
      </div>
    </div>

    <div class="tmpl-footer">
      <p-button label="Cancel" severity="secondary" [text]="true" (click)="showTemplateDialog = false" />
      <p-button label="Send template" icon="pi pi-send" (click)="sendSelectedTemplate()"
                [disabled]="!canSendTemplate" [loading]="sendingTemplate" />
    </div>
  </div>
</p-dialog>
